import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { Menu, MenuButton, MenuLink, MenuList } from "@reach/menu-button";
import { useDispatch, useSelector } from "react-redux";
import { GetTopCategoriesAction } from "./actions";
import { getTopCategories } from "./selectors";
import { buildCategoryURL } from "../../linkBuilders";
import {
  getAppURL,
  getCatalogId,
  getStoreId,
} from "../../AppContext/selectors";
import ArrowUp from "../../../assets/images/arrow-up.svg";
import ArrowDown from "../../../assets/images/arrow-down.svg";
import { previewTopCategories } from "../../../preview/previewData";
import { emitMenuOpen, useMenuAutoClose } from "./menuBus";

const StyledMenuList = styled(MenuList)`
  background-color: var(--sv-color-dark-green);
  margin-top: 5px;
  box-shadow: 8px 8px 8px 0px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  z-index: 10;
  position: relative;
  overflow: visible;
  &::after {
    content: " ";
    position: absolute;
    bottom: 100%;
    left: 52px;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: transparent transparent var(--sv-color-dark-green) transparent;
  }
  &:focus {
    outline: none;
  }
`;
const StyledMenuLink = styled(MenuLink)`
  font-family: var(--sv-font-sans);
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 44px;
  text-decoration: none;
  display: block;
  padding-left: 20px;
  padding-right: 20px;
  color: var(--sv-color-white) !important;
  border-bottom: 1px solid var(--sv-color-dark-gray);
  &[data-reach-menu-item][data-selected] {
    font-weight: bold;
  }
  &:focus {
    outline: none;
  }
`;

const CatalogMenu = ({ className, translationKey, currentFileRequired }) => {
  const [clicked, setClicked] = useState(false);
  const [isLoaded, setLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const dispatch = useDispatch();
  const categories = useSelector(getTopCategories);
  const storeId = useSelector(getStoreId);
  const catalogId = useSelector(getCatalogId);
  const appURL = useSelector(getAppURL);
  const menuId = "catalog-menu";

  useMenuAutoClose(menuId, isOpen, buttonRef);

  const isPreview = process.env.REACT_APP_RENDER_MODE === "preview";

  useEffect(() => {
    if (isPreview) {
      setLoaded(true);
      return;
    }
    handleMouseOver();
  }, []);

  const toUrl = (category) => buildCategoryURL(appURL, category.uniqueID);

  const handleMouseOver = !isLoaded
    ? () => {
        dispatch(GetTopCategoriesAction({ storeId, catalogId }));
        setLoaded(true);
      }
    : undefined;

  const handleMenuUp = (e) => {
    if (clicked) setClicked(false);
  };
  const handleMenuOut = (e) => {
    if (clicked) setClicked(false);
  };
  const handleMenuClick = (e) => {
    if (clicked) {
      e.preventDefault();
      setClicked(false);
    }
  };

  const menuCategories = isPreview ? previewTopCategories : categories;

  return (
    <Menu
      onOpen={() => {
        setIsOpen(true);
        emitMenuOpen(menuId);
      }}
      onClose={() => setIsOpen(false)}
    >
      {({ isExpanded }) => (
        <React.Fragment>
          <MenuButton
            ref={buttonRef}
            className={`primary-nav-item ${className || ""}`}
          >
            {translationKey(currentFileRequired["CATALOG"])}
            <img alt="" src={isExpanded ? ArrowUp : ArrowDown} />
          </MenuButton>
          {menuCategories && (
            <StyledMenuList>
              {menuCategories?.catalogGroupView?.map((category) => (
                <StyledMenuLink
                  key={category.uniqueID}
                  href={toUrl(category)}
                  onClick={handleMenuClick}
                  onMouseUp={handleMenuUp}
                  onMouseOut={handleMenuOut}
                >
                  {category.name}
                </StyledMenuLink>
              ))}
            </StyledMenuList>
          )}
        </React.Fragment>
      )}
    </Menu>
  );
};

CatalogMenu.propTypes = {
  className: PropTypes.string,
};

export default CatalogMenu;
